import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AuthenticationContext } from "../../controls/auth/AuthenticationWrap";
import { UserRole } from "../../../models/User";
import { MyBookingWrapper } from "../MyBooking/styled/DesStyled";
import { OrderInner } from "../Order/styled/OrderStyle";
import {
	BreadcrumbItem,
	BreadcrumbSeparator,
	DebtBreadcrumb,
	DebtContainer,
	DebtTitle,
} from "../DebtManagement/styled/DebtManagementStyles";
import {
	AdminAuditLogRecord,
	getAdminAuditLogs,
} from "../../../services/AuditLogService";

type FiltersState = {
	partnerId: string;
	changedByUserId: string;
	field: string;
	source: string;
};

const SOURCE_OPTIONS = [
	{ value: "", label: "All sources" },
	{ value: "single-edit", label: "Single edit" },
	{ value: "bulk-edit", label: "Bulk edit" },
];

const FIELD_OPTIONS = [
	{ value: "", label: "All fields" },
	{ value: "fee", label: "Fee" },
	{ value: "insuranceFee", label: "Insurance Fee" },
	{ value: "pubRate", label: "Pub Rate" },
	{ value: "stationCom", label: "Station Com" },
	{ value: "minimumPayout", label: "Minimum Payout" },
	{ value: "maximumPayout", label: "Maximum Payout" },
	{ value: "rateLimitMin", label: "Rate Limit Min" },
	{ value: "rateLimitMax", label: "Rate Limit Max" },
	{
		value: "pendingAdjustment",
		label: "Pending adjustment (PartnerBalanceAdjustments)",
	},
];

const FIELD_LABELS: Record<string, string> = {
	fee: "Fee",
	insuranceFee: "Insurance Fee",
	pubRate: "Pub Rate",
	stationCom: "Station Com",
	minimumPayout: "Minimum Payout",
	maximumPayout: "Maximum Payout",
	rateLimitMin: "Rate Limit Min",
	rateLimitMax: "Rate Limit Max",
	pendingAdjustment: "Pending adjustment",
};

function parseOptionalPositiveInt(value: string) {
	const trimmed = value.trim();
	if (!trimmed) return undefined;
	const parsed = Number.parseInt(trimmed, 10);
	return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

function formatNumberValue(value: number | null | undefined) {
	if (value == null || !Number.isFinite(value)) return "-";
	return new Intl.NumberFormat("en-US", {
		maximumFractionDigits: 6,
	}).format(value);
}

function formatDateTimeVn(value: string) {
	if (!value) return "-";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return value;
	return new Intl.DateTimeFormat("vi-VN", {
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
		second: "2-digit",
		hour12: false,
		timeZone: "Asia/Ho_Chi_Minh",
	}).format(date);
}

function getPartnerLabel(log: AdminAuditLogRecord) {
	const partnerName = log.partner?.displayName || log.partner?.name;
	if (partnerName) return `${partnerName} (#${log.partnerId})`;
	return `#${log.partnerId}`;
}

function getChangedByLabel(log: AdminAuditLogRecord) {
	const fullName = log.changedByUser?.fullName?.trim();
	const email = log.changedByUser?.email?.trim();
	if (fullName && email) return `${fullName} (${email})`;
	if (fullName) return fullName;
	if (email) return email;
	if (log.changedByUserId) return `User #${log.changedByUserId}`;
	return "System";
}

function getFieldLabel(field: string | null | undefined) {
	if (!field) return "-";
	return FIELD_LABELS[field] || field;
}

function formatAuditTypeLabel(auditType: string | null | undefined) {
	if (auditType === "partner-balance-adjustment") return "Balance adjustment";
	if (auditType === "partner-price") return "Station price";
	return auditType || "-";
}

function formatSourceLabel(log: AdminAuditLogRecord) {
	if (log.auditType === "partner-balance-adjustment") {
		const op = (log.source || "").toLowerCase();
		if (op === "add") return "add (credit)";
		if (op === "subtract") return "subtract";
		return log.source || "-";
	}
	return log.source || "-";
}

export default function AuditLogsPage() {
	const authContext = useContext(AuthenticationContext);
	const { userInfo, isFinishedValidate } = authContext || {};
	const isDenied = isFinishedValidate && (!userInfo || userInfo.role !== UserRole.Admin);

	const [loading, setLoading] = useState(false);
	const [logs, setLogs] = useState<AdminAuditLogRecord[]>([]);
	const [page, setPage] = useState(1);
	const [perPage] = useState(50);
	const [total, setTotal] = useState(0);
	const [filters, setFilters] = useState<FiltersState>({
		partnerId: "",
		changedByUserId: "",
		field: "",
		source: "",
	});
	const [appliedFilters, setAppliedFilters] = useState<FiltersState>({
		partnerId: "",
		changedByUserId: "",
		field: "",
		source: "",
	});

	const totalPages = useMemo(() => {
		if (!total) return 1;
		return Math.max(1, Math.ceil(total / perPage));
	}, [perPage, total]);

	const load = useCallback(async () => {
		if (isDenied) return;
		setLoading(true);
		try {
			const resp = await getAdminAuditLogs({
				page,
				perPage,
				partnerId: parseOptionalPositiveInt(appliedFilters.partnerId),
				changedByUserId: parseOptionalPositiveInt(appliedFilters.changedByUserId),
				field: appliedFilters.field || undefined,
				source: appliedFilters.source || undefined,
			});
			setLogs(resp.docs || []);
			setTotal(resp.total || 0);
		} catch (error: any) {
			alert(`Có lỗi xảy ra: ${error?.response?.data?.message || error.message || "Vui lòng thử lại"}`);
		} finally {
			setLoading(false);
		}
	}, [appliedFilters, isDenied, page, perPage]);

	useEffect(() => {
		load();
	}, [load]);

	const onApplyFilters = useCallback(() => {
		setPage(1);
		setAppliedFilters(filters);
	}, [filters]);

	const onResetFilters = useCallback(() => {
		const next = {
			partnerId: "",
			changedByUserId: "",
			field: "",
			source: "",
		};
		setFilters(next);
		setAppliedFilters(next);
		setPage(1);
	}, []);

	if (isDenied) {
		return (
			<MyBookingWrapper>
				<OrderInner>
					<div style={{ textAlign: "center", padding: "50px" }}>
						<h2>Access Denied</h2>
						<p>You don't have permission to access this page.</p>
					</div>
				</OrderInner>
			</MyBookingWrapper>
		);
	}

	return (
		<DebtContainer>
			<DebtBreadcrumb>
				<BreadcrumbItem>Admin</BreadcrumbItem>
				<BreadcrumbSeparator src="./static/img/direction-right.png" alt=">" />
				<BreadcrumbItem>Audit Logs</BreadcrumbItem>
			</DebtBreadcrumb>

			<DebtTitle>Audit Logs</DebtTitle>

			<div
				style={{
					background: "#fff",
					borderRadius: 16,
					padding: 16,
					boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
					marginBottom: 16,
				}}
			>
				<div
					style={{
						display: "grid",
						gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
						gap: 12,
						alignItems: "end",
					}}
				>
					<div>
						<div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Partner ID</div>
						<input
							value={filters.partnerId}
							onChange={(e) => setFilters((prev) => ({ ...prev, partnerId: e.target.value }))}
							placeholder="VD: 208"
							style={inputStyle}
						/>
					</div>
					<div>
						<div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Changed By User ID</div>
						<input
							value={filters.changedByUserId}
							onChange={(e) => setFilters((prev) => ({ ...prev, changedByUserId: e.target.value }))}
							placeholder="VD: 1"
							style={inputStyle}
						/>
					</div>
					<div>
						<div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Field</div>
						<select
							value={filters.field}
							onChange={(e) => setFilters((prev) => ({ ...prev, field: e.target.value }))}
							style={inputStyle}
						>
							{FIELD_OPTIONS.map((option) => (
								<option key={option.value} value={option.value}>
									{option.label}
								</option>
							))}
						</select>
					</div>
					<div>
						<div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Source</div>
						<select
							value={filters.source}
							onChange={(e) => setFilters((prev) => ({ ...prev, source: e.target.value }))}
							style={inputStyle}
						>
							{SOURCE_OPTIONS.map((option) => (
								<option key={option.value} value={option.value}>
									{option.label}
								</option>
							))}
						</select>
					</div>
					<div style={{ display: "flex", gap: 8 }}>
						<button onClick={onApplyFilters} style={primaryButtonStyle}>
							Apply
						</button>
						<button onClick={onResetFilters} style={secondaryButtonStyle}>
							Reset
						</button>
					</div>
				</div>
			</div>

			<div
				style={{
					background: "#fff",
					borderRadius: 16,
					padding: 16,
					boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
				}}
			>
				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						gap: 12,
						marginBottom: 12,
					}}
				>
					<div style={{ fontSize: 14, color: "#475569" }}>
						Showing {logs.length} / {total} records
					</div>
					<div style={{ fontSize: 14, color: "#475569" }}>
						Type: <strong>Price edits & PartnerBalanceAdjustments</strong>
					</div>
				</div>

				<div style={{ overflowX: "auto" }}>
					<table style={{ width: "100%", borderCollapse: "collapse", minWidth: 980 }}>
						<thead>
							<tr style={{ background: "#f8fafc" }}>
								{[
									"Changed At (VN)",
									"Partner",
									"Field",
									"Old Value",
									"New Value",
									"Source / Op",
									"Txn amount",
									"Reason",
									"Changed By",
									"Audit Type",
								].map((title) => (
									<th key={title} style={tableHeadStyle}>
										{title}
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{loading ? (
								<tr>
									<td colSpan={10} style={emptyCellStyle}>
										Loading audit logs...
									</td>
								</tr>
							) : logs.length === 0 ? (
								<tr>
									<td colSpan={10} style={emptyCellStyle}>
										No audit logs found for the current filters.
									</td>
								</tr>
							) : (
								logs.map((log) => (
									<tr
										key={`${log.auditType || "row"}-${log.id}`}
										style={{ borderTop: "1px solid #e2e8f0" }}
									>
										<td style={tableCellStyle}>{formatDateTimeVn(log.changedAt)}</td>
										<td style={tableCellStyle}>{getPartnerLabel(log)}</td>
										<td style={tableCellStyle}>{getFieldLabel(log.field)}</td>
										<td style={tableCellStyle}>{formatNumberValue(log.oldValue)}</td>
										<td style={tableCellStyle}>{formatNumberValue(log.newValue)}</td>
										<td style={tableCellStyle}>{formatSourceLabel(log)}</td>
										<td style={tableCellStyle}>
											{log.adjustmentAmount != null && Number.isFinite(log.adjustmentAmount)
												? formatNumberValue(log.adjustmentAmount)
												: "-"}
										</td>
										<td style={{ ...tableCellStyle, maxWidth: 220 }}>
											{log.reason ? (
												<span title={log.reason}>
													{log.reason.length > 80 ? `${log.reason.slice(0, 80)}…` : log.reason}
												</span>
											) : (
												"-"
											)}
										</td>
										<td style={tableCellStyle}>{getChangedByLabel(log)}</td>
										<td style={tableCellStyle}>{formatAuditTypeLabel(log.auditType)}</td>
									</tr>
								))
							)}
						</tbody>
					</table>
				</div>

				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						marginTop: 16,
						gap: 12,
						flexWrap: "wrap",
					}}
				>
					<div style={{ fontSize: 14, color: "#475569" }}>
						Page {page} / {totalPages}
					</div>
					<div style={{ display: "flex", gap: 8 }}>
						<button
							style={secondaryButtonStyle}
							onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
							disabled={page <= 1 || loading}
						>
							Prev
						</button>
						<button
							style={secondaryButtonStyle}
							onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
							disabled={page >= totalPages || loading}
						>
							Next
						</button>
					</div>
				</div>
			</div>
		</DebtContainer>
	);
}

const inputStyle: React.CSSProperties = {
	width: "100%",
	border: "1px solid #cbd5e1",
	borderRadius: 10,
	padding: "10px 12px",
	fontSize: 14,
	background: "#fff",
};

const primaryButtonStyle: React.CSSProperties = {
	border: "none",
	borderRadius: 10,
	padding: "10px 16px",
	background: "#0f62fe",
	color: "#fff",
	fontWeight: 600,
	cursor: "pointer",
};

const secondaryButtonStyle: React.CSSProperties = {
	border: "1px solid #cbd5e1",
	borderRadius: 10,
	padding: "10px 16px",
	background: "#fff",
	color: "#0f172a",
	fontWeight: 600,
	cursor: "pointer",
};

const tableHeadStyle: React.CSSProperties = {
	padding: "12px 10px",
	textAlign: "left",
	fontSize: 13,
	fontWeight: 700,
	color: "#334155",
	borderBottom: "1px solid #e2e8f0",
	whiteSpace: "nowrap",
};

const tableCellStyle: React.CSSProperties = {
	padding: "12px 10px",
	fontSize: 14,
	color: "#0f172a",
	verticalAlign: "top",
};

const emptyCellStyle: React.CSSProperties = {
	padding: "32px 12px",
	textAlign: "center",
	color: "#64748b",
	fontSize: 14,
};
